import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRACTICE_AREAS, STAGES } from "@/lib/content";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { breadcrumbSchema } from "@/lib/schemas";
import { trackIntakeStart, trackIntakeSubmit } from "@/lib/analytics";

const STEPS = [
  { no: "I", label: "Principal" },
  { no: "II", label: "Matter" },
  { no: "III", label: "Particulars" },
  { no: "IV", label: "Submit" },
];

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  practice_area: "",
  entity_type: "",
  stage: "",
  estimated_value: "",
  timeline: "",
  location: "",
  message: "",
  preferred_contact: "email",
};

export default function Intake() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    ...EMPTY,
    full_name: user && user !== false ? user.name || "" : "",
    email: user && user !== false ? user.email || "" : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [startFired, setStartFired] = useState(false);

  const update = (k, v) => {
    if (!startFired) {
      trackIntakeStart("intake_page");
      setStartFired(true);
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const canNext = () => {
    if (step === 0) return form.full_name.trim() && form.email.trim();
    if (step === 1) return form.practice_area && form.stage;
    if (step === 2) return form.message.trim().length >= 10;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/intake", form);
      trackIntakeSubmit("intake_page", {
        practice_area: form.practice_area || null,
        stage: form.stage || null,
      });
      setDone(true);
      toast.success("Received. For a time-sensitive matter, call (972) 712-1515.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const bookingUrl = process.env.REACT_APP_BOOKING_URL;
    return (
      <div className="bg-ivory min-h-screen pt-40 pb-32" data-testid="intake-success">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="kicker">Received</div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98]">
            Your conference request <br/>
            <span className="italic text-oxblood">has been received.</span>
          </h1>
          <p className="mt-10 text-lg text-slate2 font-light leading-relaxed">
            A partner will review your particulars personally. For a time-sensitive matter,
            please call <a href="tel:+19727121515" className="text-oxblood underline underline-offset-4 hover:text-brass" data-testid="intake-success-tel">(972) 712-1515</a>.
            We extend our thanks for your trust &mdash; and our discretion in handling what you have shared.
          </p>

          {/* Optional: reserve a preferred time on the firm's calendar */}
          {bookingUrl && (
            <div className="mt-16 hairline-top pt-12" data-testid="intake-booking-block">
              <div className="kicker">Reserve a Time</div>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-[1.1]">
                Prefer to <span className="italic text-oxblood">select a time now?</span>
              </h2>
              <p className="mt-4 text-slate2 font-light leading-relaxed max-w-xl mx-auto">
                Reserve a private consultation on the firm&apos;s Outlook calendar via
                Microsoft Bookings. Complimentary — the first hour of counsel is on the firm.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-brass inline-flex items-center gap-3"
                  data-testid="intake-booking-cta"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 23 23"
                    aria-hidden="true"
                    focusable="false"
                    className="shrink-0"
                  >
                    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                  </svg>
                  Book with Microsoft →
                </a>
              </div>
            </div>
          )}

          <div className="mt-14 flex justify-center gap-4">
            {user && user !== false ? (
              <button onClick={() => navigate("/portal")} className="btn-primary" data-testid="intake-success-portal">
                View in Portal
              </button>
            ) : (
              <button onClick={() => navigate("/")} className="btn-primary" data-testid="intake-success-home">
                Return Home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-bold bg-ivory min-h-screen pt-32 md:pt-40 pb-32" data-testid="intake-page">
      <SEO
        title="Request a Conference — Begin a Private Engagement"
        description="Begin your matter with Lone Star Counsel in four steps. Available at our Frisco, Round Rock, Boerne, The Woodlands and Sugar Land offices."
        path="/intake"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Request a Conference", path: "/intake" },
        ])}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: title + steps */}
          <aside className="lg:col-span-4">
            <div className="kicker">Engage the Firm</div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              Request a private<br/>
              <span className="italic font-cormorant text-oxblood">conference.</span>
            </h1>
            <p className="mt-8 text-lg text-ink font-normal leading-relaxed">
              Four short steps. Two business days for a partner response. Confidential and
              privileged from your first keystroke.
            </p>

            <ol className="mt-12 space-y-5 hidden lg:block" data-testid="intake-steps">
              {STEPS.map((s, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-5 border-l-2 pl-5 py-1 ${
                    i === step ? "border-oxblood" : i < step ? "border-brass" : "border-transparent"
                  }`}
                  data-testid={`intake-step-${i}`}
                >
                  <span className={`serif-num text-3xl font-semibold ${i <= step ? "text-oxblood" : "text-slate2/70"}`}>
                    {i < step ? <Check size={22} /> : s.no}
                  </span>
                  <span className={`font-serif text-2xl ${i === step ? "text-ink font-semibold" : "text-ink/70"}`}>
                    {s.label}
                  </span>
                </li>
              ))}
            </ol>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-7 lg:col-start-6">
            <div data-testid="intake-form">
              <div className="mb-10" data-testid="intake-progress">
                <div className="flex items-baseline justify-between text-sm font-semibold uppercase tracking-[0.18em] text-ink">
                  <span data-testid="intake-progress-label">Step {step + 1} of {STEPS.length}</span>
                  <span className="text-oxblood">{STEPS[step].label}</span>
                </div>
                <div className="mt-3 h-2 w-full bg-navy/10" role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={step + 1}>
                  <div className="h-full bg-oxblood transition-[width] duration-500 ease-out" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
                </div>
              </div>
              {step === 0 && (
                <div className="space-y-8" data-testid="step-principal">
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">The Principal</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Field label="Full Name" required>
                      <input className="field" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="intake-full-name" />
                    </Field>
                    <Field label="Email Address" required>
                      <input className="field" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="intake-email" />
                    </Field>
                    <Field label="Telephone">
                      <input className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="intake-phone" />
                    </Field>
                    <Field label="Preferred Contact">
                      <select className="field" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="intake-preferred-contact">
                        <option value="email">By Email</option>
                        <option value="phone">By Telephone</option>
                        <option value="post">By Post</option>
                      </select>
                    </Field>
                    <Field label="Location (City, State)">
                      <input className="field" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="intake-location" placeholder="Austin, Texas" />
                    </Field>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-8" data-testid="step-matter">
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">The Matter</h2>
                  <Field label="Practice Area" required>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {PRACTICE_AREAS.map((p) => (
                        <button
                          key={p.slug}
                          type="button"
                          onClick={() => update("practice_area", p.title)}
                          data-testid={`intake-practice-${p.slug}`}
                          className={`px-4 py-3 text-sm border-[1.5px] text-left transition-all ${
                            form.practice_area === p.title
                              ? "bg-navy text-ivory border-navy"
                              : "bg-white text-ink border-navy/30 hover:border-navy"
                          }`}
                        >
                          <div className={`serif-num text-sm font-semibold ${form.practice_area === p.title ? "text-brass" : "text-oxblood"}`}>{p.no}</div>
                          <div className={`mt-1 font-serif text-lg font-semibold ${form.practice_area === p.title ? "text-ivory" : "text-ink"}`}>{p.title}</div>
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Stage of the Continuum" required>
                    <div className="flex flex-wrap gap-2">
                      {STAGES.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => update("stage", s.key)}
                          data-testid={`intake-stage-${s.key}`}
                          className={`px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em] border-[1.5px] transition ${
                            form.stage === s.key
                              ? "bg-oxblood text-ivory border-oxblood"
                              : "bg-white text-ink border-navy/30 hover:border-oxblood"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Field label="Entity Type">
                      <input className="field" value={form.entity_type} onChange={(e) => update("entity_type", e.target.value)} data-testid="intake-entity-type" placeholder="LLC · LP · Trust · Individual" />
                    </Field>
                    <Field label="Estimated Value at Issue">
                      <input className="field" value={form.estimated_value} onChange={(e) => update("estimated_value", e.target.value)} data-testid="intake-value" placeholder="$1M–$10M" />
                    </Field>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8" data-testid="step-particulars">
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">The Particulars</h2>
                  <Field label="Timeline">
                    <input className="field" value={form.timeline} onChange={(e) => update("timeline", e.target.value)} data-testid="intake-timeline" placeholder="Within 30 days · This quarter · Exploratory" />
                  </Field>
                  <Field label="Tell us, in your own words, what you would like to discuss" required>
                    <textarea
                      className="field"
                      rows={8}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      data-testid="intake-message"
                      placeholder="Be candid — this conversation is privileged. The more we understand, the better we may serve."
                    />
                  </Field>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8" data-testid="step-review">
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">Review & Submit</h2>
                  <p className="text-lg text-ink font-normal leading-relaxed">
                    Confirm the particulars below. By submitting, you understand that this submission
                    does not create an attorney–client relationship; the firm will respond personally
                    within two business days.
                  </p>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-10 hairline pt-8">
                    <Item label="Principal" value={form.full_name} />
                    <Item label="Email" value={form.email} />
                    <Item label="Telephone" value={form.phone || "—"} />
                    <Item label="Location" value={form.location || "—"} />
                    <Item label="Practice" value={form.practice_area} />
                    <Item label="Stage" value={STAGES.find((s) => s.key === form.stage)?.label || "—"} />
                    <Item label="Entity" value={form.entity_type || "—"} />
                    <Item label="Estimated Value" value={form.estimated_value || "—"} />
                  </dl>
                  <div className="hairline pt-6">
                    <div className="kicker">Message</div>
                    <p className="mt-3 text-lg text-ink leading-relaxed">"{form.message}"</p>
                  </div>
                </div>
              )}

              {/* Nav */}
              <div className="mt-12 flex items-center justify-between hairline pt-8">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="nav-link font-semibold text-ink disabled:opacity-30"
                  data-testid="intake-back-button"
                >
                  <ArrowLeft size={16} className="inline mr-2" /> Back
                </button>
                {step < 3 ? (
                  <button
                    type="button"
                    onClick={() => canNext() && setStep((s) => s + 1)}
                    disabled={!canNext()}
                    className="btn-primary disabled:opacity-40"
                    data-testid="intake-next-button"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    className="btn-brass disabled:opacity-40"
                    data-testid="intake-submit-button"
                  >
                    {submitting ? "Submitting…" : "Submit to the Firm"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">
        {label} {required && <span className="text-oxblood" aria-label="required">*</span>}
      </div>
      {children}
    </label>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <div className="kicker text-xs font-semibold">{label}</div>
      <div className="mt-1 text-lg font-medium text-ink">{value || "—"}</div>
    </div>
  );
}
